import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App.js';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog.jsx';
import { Textarea } from './ui/textarea.jsx';
import { useReactToPrint } from 'react-to-print';
import {
  Building2,
  Plus,
  Search,
  Filter,
  Users,
  CreditCard,
  TrendingUp,
  Calendar,
  LogOut,
  Settings,
  FolderOpen,
  Eye,
  Printer
} from 'lucide-react';

const ProjectReport = React.forwardRef(({ project }, ref) => {
  const totalSuppliers = Number(project.total_suppliers) || 0;
  const totalPayments = Number(project.total_payments) || 0;

  const cardStyle = {
    backgroundColor: '#f9fafb',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '30px',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '10px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thTdStyle = {
    border: '1px solid #ddd',
    padding: '10px'
  };

  const thStyle = {
    ...thTdStyle,
    backgroundColor: '#f3f4f6',
    textAlign: 'left'
  };

  const sectionTitleStyle = {
    borderLeft: '5px solid #10b981',
    paddingLeft: '10px',
    marginBottom: '10px',
    fontWeight: 'bold'
  };

  return (
    <div ref={ref} style={{ fontFamily: 'Arial, sans-serif', color: '#111', padding: '30px', backgroundColor: '#fff', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={{ fontSize: '28px', color: '#10b981', margin: 0 }}>Rapport du Projet</h1>
        <h2 style={{ fontSize: '20px', margin: '5px 0' }}>{project.name || 'Projet sans nom'}</h2>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Généré le {new Date().toLocaleDateString('fr-FR')}</p>
      </div>

      {/* Description */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Description</h3>
        <p>{project.description || 'Aucune description fournie'}</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
        <div style={{ ...cardStyle, flex: 1, borderLeft: '5px solid #10b981', textAlign: 'center' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px' }}>Nombre de Fournisseurs</p>
          <p style={{ fontSize: '24px', color: '#10b981', margin: '5px 0' }}>{totalSuppliers}</p>
        </div>
        <div style={{ ...cardStyle, flex: 1, borderLeft: '5px solid #3b82f6', textAlign: 'center' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px' }}>Total des Paiements</p>
          <p style={{ fontSize: '24px', color: '#3b82f6', margin: '5px 0' }}>{totalPayments.toLocaleString()} MAD</p>
        </div>
      </div>

      {/* Payments Table */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Liste des Fournisseurs et des Paiements</h3>
        {project.payments && project.payments.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Fournisseur</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Montant (MAD)</th>
              </tr>
            </thead>
            <tbody>
              {project.payments.map((p, idx) => (
                <tr key={idx}>
                  <td style={thTdStyle}>{p.supplier_name || '-'}</td>
                  <td style={{ ...thTdStyle, textAlign: 'right' }}>{Number(p.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Aucun paiement enregistré</p>
        )}
      </div>

      {/* Footer */}
      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
        <p>Date de création: {project.created_at ? new Date(project.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
        <p style={{ fontStyle: 'italic' }}>Rapport généré automatiquement</p>
      </div>
    </div>
  );
});


ProjectReport.displayName = 'ProjectReport';

const ProjectCard = ({ project, onPrint }) => {
  const totalSuppliers = Number(project.total_suppliers) || 0;
  const totalPayments = Number(project.total_payments) || 0;

  return (
    <Card className="glass hover-lift slide-up group">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg text-slate-800 group-hover:text-emerald-600 transition-colors">
              {project.name}
            </CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {project.description || 'Aucune description fournie'}
            </CardDescription>
          </div>
          <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-lg flex items-center justify-center ml-3">
            <Building2 className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-lg font-semibold text-slate-800">{totalSuppliers}</p>
            <p className="text-xs text-slate-600">Fournisseurs</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-lg font-semibold text-slate-800">
              {totalPayments.toLocaleString()} MAD
            </p>
            <p className="text-xs text-slate-600">Total Payé</p>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {project.created_at ? new Date(project.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            Actif
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/projects/${project.id}`} className="flex-1">
            <Button className="w-full group-hover:bg-emerald-600 transition-colors">
              <Eye className="w-4 h-4 mr-2" />
              Voir Détails
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => onPrint(project)}
            className="flex-none px-3"
            title="Imprimer le rapport"
          >
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const componentRef = useRef(null);
  const { user, logout, API } = useAuth();
  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => {
      if (!componentRef.current) {
        console.error('componentRef.current is null in content function');
        toast.error('Échec de l\'impression : contenu non disponible');
        return null;
      }
      if (!componentRef.current.isConnected) {
        console.error('componentRef.current is not connected to the DOM');
        toast.error('Échec de l\'impression : contenu non connecté au DOM');
        return null;
      }
      console.log('Returning componentRef.current for printing:', componentRef.current);
      console.log('DOM element details:', {
        tagName: componentRef.current.tagName,
        className: componentRef.current.className,
        isConnected: componentRef.current.isConnected,
        innerHTMLLength: componentRef.current.innerHTML.length
      });
      return componentRef.current;
    },
    documentTitle: selectedProject ? `Projet_${selectedProject.name}_Rapport` : 'Rapport_Projet',
    onAfterPrint: () => {
      console.log('Print completed successfully');
      setSelectedProject(null);
    },
    onPrintError: (error) => {
      console.error('Print error:', error);
      toast.error('Échec de l\'impression : contenu non disponible');
    },
  });

  const handleManualPrint = () => {
    if (componentRef.current && componentRef.current.isConnected) {
      console.log('Attempting manual print');
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Projet_${selectedProject.name}_Rapport</title>
            <style>
              body { font-family: sans-serif; padding: 20px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ddd; padding: 8px; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            ${componentRef.current.outerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
      setSelectedProject(null);
    } else {
      console.error('Manual print failed: componentRef.current is null or not connected');
      toast.error('Échec de l\'impression manuelle : contenu non disponible');
    }
  };

  const handlePrintClick = (project) => {
    console.log('Setting selectedProject:', project);
    setSelectedProject(project);
  };

  useEffect(() => {
    if (selectedProject) {
      console.log('useEffect triggered, selectedProject:', selectedProject);
      console.log('Initial componentRef.current:', componentRef.current);
      let attempts = 0;
      const maxAttempts = 5;
      const checkRefAndPrint = () => {
        if (componentRef.current && componentRef.current.isConnected) {
          console.log('Attempting to print, componentRef.current:', componentRef.current);
          handlePrint();
          // Fallback to manual print if react-to-print fails
          setTimeout(() => {
            if (!document.querySelector('.react-to-print')) { // Check if print dialog opened
              console.log('react-to-print failed, falling back to manual print');
              handleManualPrint();
            }
          }, 1000);
        } else {
          attempts++;
          console.log(`Ref not ready, attempt ${attempts}/${maxAttempts}, ref:`, componentRef.current);
          if (attempts < maxAttempts) {
            setTimeout(checkRefAndPrint, 500);
          } else {
            console.error('Print component is not ready after max attempts');
            toast.error('Impossible d\'imprimer : contenu non disponible');
            // Try manual print as a last resort
            handleManualPrint();
          }
        }
      };
      const timeoutId = setTimeout(checkRefAndPrint, 500);
      return () => {
        console.log('Cleaning up timeout');
        clearTimeout(timeoutId);
      };
    }
  }, [selectedProject, handlePrint]);

  useEffect(() => {
    console.log('ProjectReport mounted, ref:', componentRef.current);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const projectsData = response.data;
      const projectsWithStats = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const statsRes = await axios.get(`${API}/projects/${project.id}/stats`);
            const suppliersRes = await axios.get(`${API}/projects/${project.id}/suppliers`);
            const paymentsRes = await axios.get(`${API}/projects/${project.id}/payments`);
            return {
              ...project,
              ...statsRes.data,
              suppliers: suppliersRes.data,
              payments: paymentsRes.data
            };
          } catch {
            return {
              ...project,
              total_suppliers: 0,
              total_payments: 0,
              suppliers: [],
              payments: []
            };
          }
        })
      );
      setProjects(projectsWithStats);
    } catch (error) {
      toast.error('Échec du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      toast.error('Le nom du projet est requis');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      setProjects([...projects, response.data]);
      setNewProject({ name: '', description: '' });
      setShowCreateModal(false);
      toast.success('Projet créé avec succès !');
    } catch (error) {
      toast.error('Échec de la création du projet');
    } finally {
      setCreating(false);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement de vos projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      <header className="bg-white/80 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">VINQO-project-Manager</h1>
                <p className="text-sm text-slate-600">Bienvenue, {user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="hidden sm:flex">
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Projets</p>
                  <p className="text-2xl font-bold text-slate-800">{projects.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Fournisseurs</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {projects.reduce((sum, p) => sum + (p.total_suppliers ?? 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Paiements</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {projects
                      .reduce((sum, p) => sum + (p.total_payments ?? 0), 0)
                      .toLocaleString()} MAD
                  </p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Vos Projets</h2>
            <p className="text-slate-600">Gérez vos projets et suivez les paiements des fournisseurs</p>
          </div>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Projet
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Créer un Nouveau Projet</DialogTitle>
                <DialogDescription>
                  Ajoutez un nouveau projet pour commencer à suivre les fournisseurs et paiements.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du Projet</Label>
                  <Input
                    id="name"
                    placeholder="ex: Rénovation Bureau"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="initial_budget">Montant Initial du Projet (MAD)</Label>
                  <Input
                    id="initial_budget"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newProject.initial_budget}
                    onChange={(e) => setNewProject({ ...newProject, initial_budget: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optionnel)</Label>
                  <Textarea
                    id="description"
                    placeholder="Brève description du projet..."
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Création...' : 'Créer le Projet'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher des projets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/70"
            />
          </div>
          <Button variant="outline" className="sm:w-auto">
            <Filter className="w-4 h-4 mr-2" />
            Filtrer
          </Button>
        </div>
        {/* Temporarily render ProjectReport visibly for testing */}
        <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
          <ProjectReport ref={componentRef} project={selectedProject || { suppliers: [], payments: [] }} />
        </div>
        {filteredProjects.length === 0 ? (
          <Card className="glass text-center py-12">
            <CardContent>
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                {searchTerm ? 'Aucun projet trouvé' : 'Aucun projet pour le moment'}
              </h3>
              <p className="text-slate-600 mb-6">
                {searchTerm
                  ? 'Essayez d\'ajuster vos termes de recherche'
                  : 'Créez votre premier projet pour commencer à gérer les fournisseurs et paiements'
                }
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer Votre Premier Projet
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} onPrint={handlePrintClick} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;